import { Alert, Badge, Tag, Tooltip } from 'antd';
import { PresetStatusColorTypes } from 'antd/es/_util/colors';
import classNames from 'classnames';

import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import type { ICoverPicture } from 'app/typings/migrationTool/HotelWithDetails';

import { Share } from '../Icons/Share';

import './HotelServiceCard.scss';

export enum SelectActionEnum {
  MERGE = 'MERGE',
  DO_NOT_MERGE = 'DO_NOT_MERGE',
  CREATE_NEW = 'CREATE_NEW',
}

type HotelServiceCardProps = {
  picture?: ICoverPicture;
  title: string;
  description?: string;
  isPublished?: boolean;
  redirectUrl?: string;
  bedsList?: string[];
  isChild?: boolean;
  selectAction?: SelectActionEnum;
  hotelName?: string;
  entity?: string;
};

/**
 * @param Title: Name of room, experience, package
 * @param Description: Room category, experience price, package aliasForUser
 * @param redirectUrl: Url to redirect to Admin room, experience, package page
 * @param isPublished: Indicates if the room, experience, package is published
 * @param bedsList: List of bed types associated with the room (with price information)
 * @param isChild: Indicates if the card is a child service (used in merge action)
 * @param selectAction: Action to perform on the service (merge, do not merge, create)
 * @param entity: Name of the entity (room, experience, package)
 */
export const HotelServiceCard = ({
  picture,
  title,
  description,
  redirectUrl,
  isPublished,
  bedsList,
  isChild = false,
  selectAction,
  hotelName,
  entity,
}: HotelServiceCardProps) => {
  const selectDoNotMerge = selectAction === SelectActionEnum.DO_NOT_MERGE;
  const successColor = PresetStatusColorTypes[0];
  const errorColor = PresetStatusColorTypes[2];
  const publicationStatus =
    isPublished === undefined
      ? undefined
      : isPublished
        ? { label: 'Published', status: successColor }
        : { label: 'Unpublished', status: errorColor };

  if (selectDoNotMerge && entity) {
    return (
      <div className="hotel-service-container">
        {hotelName && <div className="hotel-name">{hotelName}</div>}
        <Alert
          message={`This ${entity?.toLowerCase()} will be deleted from Admin and Extranet after migration.`}
          type="warning"
          showIcon
          className="hotel-service-card__alert"
        />
      </div>
    );
  }

  const showRedirection =
    (isChild || (!isChild && selectAction !== SelectActionEnum.CREATE_NEW)) &&
    !!redirectUrl;

  return (
    <div className="hotel-service-container">
      {hotelName && <div className="hotel-name">{hotelName}</div>}
      <div
        className={classNames('hotel-service-card', {
          'no-action-select': !selectAction && !isChild,
          'no-merge': selectDoNotMerge,
        })}
      >
        {(selectAction || isChild) && !selectDoNotMerge && (
          <>
            <div className="hotel-service-card__main">
              <div className="hotel-service-card__main__info">
                <div className="hotel-service-card__main__info__img-container">
                  <PictureComponent
                    alt={picture ? (picture.alt ?? '') : ''}
                    pictureId={
                      picture ? picture.path : 'static/default-picture.jpg'
                    }
                    className="hotel-service-card__main__info__img-container__img"
                  />
                </div>
                <div className="hotel-service-card__main__info__text">
                  <div>
                    <div className="hotel-service-card__main__info__text__title">
                      <Tooltip title={title}>{title}</Tooltip>
                    </div>
                    <div className="hotel-service-card__main__info__text__description">
                      {description ?? ''}
                    </div>
                  </div>
                  {publicationStatus && (
                    <div className="hotel-service-card__main__info__text__status">
                      <Badge status={publicationStatus.status} />
                      {publicationStatus.label}
                    </div>
                  )}
                </div>
              </div>
              {showRedirection && (
                <div
                  className="hotel-service-card__main__actions"
                  onClick={() => window.open(redirectUrl ?? '', '_blank')}
                >
                  <Share color="#8C8C8C" />
                </div>
              )}
            </div>
            {bedsList && bedsList.length > 0 && (
              <div className="hotel-service-card__tags-list">
                {bedsList.map((bed, index) => (
                  <Tag
                    key={index}
                    className="hotel-service-card__tags-list__item"
                  >
                    {bed}
                  </Tag>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
